import { request } from '@umijs/max';

export type LightCompanyStatus =
  | 'pending_release'
  | 'public'
  | 'claimed'
  | 'converted_to_task'
  | 'withdrawn';

export type LightTaskStatus =
  | 'draft'
  | 'recruiting'
  | 'paused'
  | 'closed'
  | 'completed';

export type LightTaskSalaryUnit = 'annual_wan_cny' | 'monthly_k_cny';

export type LightRecommendationStatus =
  | 'submitted'
  | 'bd_screen_passed'
  | 'recommended_to_client'
  | 'client_interested'
  | 'interviewing'
  | 'first_interview'
  | 'second_interview'
  | 'third_interview'
  | 'offer_accepted'
  | 'hired'
  | 'probation_passed'
  | 'rejected';

export type LightMemberRole =
  | 'admin'
  | 'bd'
  | 'headhunter'
  | 'part_time_headhunter';

export interface LightHunterAccessPermissions {
  viewDashboard: boolean;
  viewEnterpriseDirectory: boolean;
  manageEnterprise: boolean;
  viewTaskCenter: boolean;
  createOwnClientTask: boolean;
  manageOwnTask: boolean;
  viewTalentLibrary: boolean;
  reviewRecommendations: boolean;
  manageMembers: boolean;
  reviewVerification: boolean;
  viewFinance: boolean;
  viewAnalytics: boolean;
}

export interface LightHunterAccess {
  memberUuid: string | null;
  role: LightMemberRole | null;
  isManagement: boolean;
  permissions: LightHunterAccessPermissions;
}

export interface PageParams {
  page?: number;
  limit?: number;
  search?: string;
}

export interface PageResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export interface LightHunterDashboard {
  publicCompanies: number;
  claimedCompanies: number;
  recruitingTasks: number;
  recommendations: number;
  interviews: number;
  hired: number;
  pendingScreening?: number;
  dueSoonTasks?: number;
  overdueTasks?: number;
  pendingWithdrawals?: number;
}

export interface LightCompanyPoolItem {
  uuid: string;
  companyName: string;
  clientUuid?: string | null;
  sourceTenantUuid?: string | null;
  sourceOrganizationUuid?: string | null;
  industry?: string | null;
  location?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  status: LightCompanyStatus;
  ownerUuid?: string | null;
  bdMemberUuid?: string | null;
  bdName?: string | null;
  releasedAt?: string | null;
  claimedAt?: string | null;
  notes?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface LightEnterpriseItem {
  uuid: string;
  tenantUuid: string;
  organizationUuid?: string | null;
  tenantName?: string | null;
  tenantCode?: string | null;
  tenantDescription?: string | null;
  tenantDomain?: string | null;
  homepageCompanyName?: string | null;
  homepageLogoUrl?: string | null;
  industryCode?: string | null;
  industryName?: string | null;
  companySizeCode?: string | null;
  companySizeName?: string | null;
  companyNatureCode?: string | null;
  companyNatureName?: string | null;
  status: 'unverified' | 'pending' | 'verified' | 'rejected';
  businessLicenseUrl?: string | null;
  businessLicenseFileName?: string | null;
  remark?: string | null;
  submittedAt?: string | null;
  reviewedAt?: string | null;
  poolUuid?: string | null;
  poolStatus?: LightCompanyStatus | null;
  bdMemberUuid?: string | null;
  bdName?: string | null;
  released: boolean;
  jobPostingCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface LightEnterpriseMetrics {
  totalEnterprises: number;
  releasedEnterprises: number;
  unreleasedEnterprises: number;
  totalJobPostings: number;
  releaseRate: number;
}

export type LightEnterpriseReleaseState = 'all' | 'unreleased' | 'released';
export type LightEnterpriseSourceType = 'certified' | 'manual';
export type LightEnterpriseProgressStage =
  | 'no_job'
  | 'has_job'
  | 'has_task'
  | 'has_candidate'
  | 'interviewing'
  | 'offer'
  | 'hired';

export interface LightEnterpriseDirectoryItem {
  rowKey: string;
  sourceType: LightEnterpriseSourceType;
  certificationUuid?: string | null;
  channelUuid?: string | null;
  clientUuid?: string | null;
  tenantUuid?: string | null;
  organizationUuid?: string | null;
  companyName: string;
  logoUrl?: string | null;
  industryCode?: string | null;
  industryName?: string | null;
  location?: string | null;
  certificationStatus?: LightEnterpriseItem['status'] | null;
  channelStatus?: LightCompanyStatus | null;
  released: boolean;
  bdMemberUuid?: string | null;
  bdName?: string | null;
  jobPostingCount: number;
  taskCount: number;
  progressStage?: LightEnterpriseProgressStage;
  certifiedAt?: string | null;
  releasedAt?: string | null;
  claimedAt?: string | null;
  updatedAt?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  notes?: string | null;
  introduction?: string | null;
}

export interface LightEnterpriseDirectoryIndustryOption {
  value: string;
  label: string;
  count: number;
}

export interface LightEnterpriseDirectoryProgressCounts {
  no_job: number;
  has_job: number;
  has_task: number;
  has_candidate: number;
  interviewing: number;
  offer: number;
  hired: number;
}

export interface LightEnterpriseDirectorySummary {
  totalEnterprises: number;
  releasedEnterprises: number;
  unreleasedEnterprises: number;
  manualEnterprises: number;
  publicCompanies?: number;
  industries?: LightEnterpriseDirectoryIndustryOption[];
  progress?: LightEnterpriseDirectoryProgressCounts;
}

export interface LightEnterpriseDirectoryResponse
  extends PageResponse<LightEnterpriseDirectoryItem> {
  summary: LightEnterpriseDirectorySummary;
}

export interface LightReleasedJobPostingItem {
  uuid: string;
  title: string;
  companyName: string;
  companyPoolUuid: string;
  location?: string | null;
  category?: string | null;
  salary?: string | null;
  status: string;
  publishedAt?: string | null;
  bdName?: string | null;
  taskUuid?: string | null;
  taskTitle?: string | null;
  taskStatus?: string | null;
}

export interface LightEnterpriseJobPostingItem {
  uuid: string;
  title: string;
  description?: string | null;
  requirements?: string | null;
  responsibilities?: string | null;
  category?: string | null;
  level?: string | null;
  workType?: string | null;
  location?: string | null;
  locationDetail?: string | null;
  minSalary?: number | null;
  maxSalary?: number | null;
  salaryUnit?: string | null;
  salaryNegotiable?: boolean;
  salaryMonths?: number | null;
  salary?: string | null;
  minExperience?: number | null;
  maxExperience?: number | null;
  minEducation?: string | null;
  skills?: string | null;
  skillTags?: string[];
  benefits?: string | null;
  benefitTags?: string[];
  hiringCount?: number;
  applicationCount?: number;
  interviewCount?: number;
  hiredCount?: number;
  status: 'draft' | 'published' | 'closed' | 'archived';
  urgency?: string | null;
  applicationDeadline?: string | null;
  publishedAt?: string | null;
  updatedAt?: string | null;
}

export interface LightEnterpriseDetail extends LightEnterpriseItem {
  reviewerUserUuid?: string | null;
  website?: string | null;
  address?: string | null;
  metrics: {
    totalJobs: number;
    activeJobs: number;
    successRate: number;
    applicationCount: number;
    hiredCount: number;
  };
  contact?: {
    name?: string | null;
    phone?: string | null;
    email?: string | null;
  };
  jobs: LightEnterpriseJobPostingItem[];
}

export interface LightTalentItem {
  uuid: string;
  candidateUuid: string;
  name: string;
  phone?: string | null;
  email?: string | null;
  weixin?: string | null;
  showContact: boolean;
  contactMasked: boolean;
  city?: string | null;
  workCompany?: string | null;
  workPosition?: string | null;
  workYear?: string | null;
  expectJob?: string | null;
  expectLocation?: string | null;
  expectSalary?: string | null;
  jobStatus?: string | null;
  isPublic: boolean;
  allowSearch?: boolean | null;
  status?: string | null;
  isCurrent: boolean;
  isPrimary: boolean;
  resumeName?: string | null;
  resumeUpdateTime?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ReleaseCompanyPayload {
  companyName: string;
  clientUuid?: string;
  sourceTenantUuid?: string;
  sourceOrganizationUuid?: string;
  industry?: string;
  location?: string;
  contactName?: string;
  contactPhone?: string;
  notes?: string;
  introduction?: string;
  bdMemberUuid?: string;
  ownerUuid?: string;
  bindingConfirmation?: boolean;
}

export interface ReleaseEnterprisePayload {
  clientUuid?: string;
  bindingConfirmation?: boolean;
  notes?: string;
  bdMemberUuid?: string;
  ownerUuid?: string;
}

export interface BatchReleaseEnterprisePayload
  extends ReleaseEnterprisePayload {
  certificationUuids: string[];
  clientBindings?: Record<string, string>;
}

export interface BatchReleaseEnterpriseResult {
  total: number;
  successCount: number;
  skippedCount: number;
  failureCount: number;
  needsConfirmationCount?: number;
  ambiguousCount?: number;
  successes: Array<{
    certificationUuid: string;
    lightHunterChannel: LightCompanyPoolItem;
  }>;
  skipped: Array<{ certificationUuid: string; reason: string }>;
  failures: Array<{ certificationUuid: string; reason: string }>;
  needsConfirmation?: Array<{ certificationUuid: string; reason: string }>;
  ambiguous?: Array<{ certificationUuid: string; reason: string }>;
}

export interface LightEnterpriseReleaseCandidate {
  clientUuid: string;
  customerId: number;
  companyName: string;
  ownerUuid?: string | null;
  ownerName?: string | null;
  poolType: 'public' | 'private';
  source?: string | null;
  industry?: string | null;
  location?: string | null;
  existingLightHunterChannelUuid?: string | null;
}

export interface LightEnterpriseReleasePreview {
  certification: {
    uuid: string;
    sourceTenantUuid: string;
    companyName: string;
    industry?: string | null;
    location?: string | null;
  };
  binding: {
    mode: 'source_tenant' | 'explicit_client' | 'candidate' | 'create';
    requiresConfirmation: boolean;
    reason: string;
  };
  candidates: LightEnterpriseReleaseCandidate[];
}

export interface LightTaskItem {
  uuid: string;
  title: string;
  companyPoolUuid: string;
  companyName?: string | null;
  clientUuid?: string | null;
  jobUuid?: string | null;
  projectUuid?: string | null;
  projectName?: string | null;
  projectStatus?: string | null;
  projectJobUuid?: string | null;
  sourceJobUuid?: string | null;
  bdMemberUuid?: string | null;
  bdName?: string | null;
  status: LightTaskStatus;
  jobTitle?: string | null;
  category?: string | null;
  location?: string | null;
  salaryUnit?: LightTaskSalaryUnit | null;
  salaryMin?: number | null;
  salaryMax?: number | null;
  educationRequirement?: string | null;
  experienceRequirement?: string | null;
  rewardRule?: string | null;
  commissionAmount?: number | null;
  rewardStages?: LightHunterRewardStage[] | null;
  rewardMode?: 'points' | 'cash_legacy';
  totalPoints?: number;
  difficultyLevel?: 'normal' | 'hard' | 'difficult';
  difficultyMultiplier?: number;
  settlementCondition?: string | null;
  headhunterSplitPercent?: number | null;
  bdSplitPercent?: number | null;
  targetGroupNames?: string[] | null;
  recommendationRequirement?: string | null;
  description?: string | null;
  deadline?: string | null;
  publishedAt?: string | null;
  closedAt?: string | null;
  activeClaimCount?: number;
  recommendationCount?: number;
  settlementCount?: number;
  lastActivityAt?: string;
  editPolicy?: LightTaskEditPolicy;
  createdAt?: string;
  updatedAt?: string;
}

export interface LightTaskEditPolicy {
  canEdit: boolean;
  editableFields: string[];
  rewardLocked: boolean;
  reason?: string | null;
}

export interface LightHunterRewardStage {
  stageCode:
    | 'recommended'
    | 'interview'
    | 'interview_1'
    | 'interview_2'
    | 'interview_3'
    | 'offer'
    | 'hired'
    | 'probation';
  triggerStatus:
    | 'bd_screen_passed'
    | 'interviewing'
    | 'first_interview'
    | 'second_interview'
    | 'third_interview'
    | 'offer_accepted'
    | 'hired'
    | 'probation_passed';
  label: string;
  description?: string | null;
  amount: number;
  basePoints?: number;
  mode?: 'percent' | 'fixed';
  sortOrder: number;
}

export interface LightTaskCompanyOption {
  value: string;
  label: string;
  source: 'channel' | 'private_client' | 'job_client';
  companyPoolUuid?: string | null;
  clientUuid?: string | null;
  sourceTenantUuid?: string | null;
  companyName?: string | null;
  introduction?: string | null;
  introductionEditable?: boolean;
  status?: LightCompanyStatus | null;
}

export interface LightTaskStatusCounts {
  draft: number;
  recruiting: number;
  paused: number;
  closed: number;
  completed: number;
}

export interface LightTaskListSummary {
  total: number;
  byStatus: LightTaskStatusCounts;
}

export interface CreateTaskPayload {
  companyPoolUuid?: string;
  clientUuid?: string;
  bdMemberUuid?: string;
  title: string;
  jobTitle?: string;
  category?: string;
  location?: string;
  salaryUnit?: LightTaskSalaryUnit;
  salaryMin?: number;
  salaryMax?: number;
  educationRequirement?: string;
  experienceRequirement?: string;
  rewardRule?: string;
  commissionAmount?: number;
  rewardStages?: LightHunterRewardStage[];
  rewardMode?: 'points' | 'cash_legacy';
  totalPoints?: number;
  difficultyLevel?: 'normal' | 'hard' | 'difficult';
  difficultyMultiplier?: number;
  settlementCondition?: string;
  headhunterSplitPercent?: number;
  bdSplitPercent?: number;
  targetGroupNames?: string[];
  recommendationRequirement?: string;
  companyIntroduction?: string;
  description?: string;
  deadline?: string;
  sourceJobUuid?: string;
  projectUuid?: string | null;
}

export interface LightTaskProjectSyncSummary {
  added: number;
  exists: number;
  skipped: number;
  failed: number;
}

export interface LightRecommendationProjectSync {
  status: 'added' | 'exists' | 'skipped' | 'unbound' | 'failed';
  reason?: string;
  projectCandidateUuid?: string | null;
}

export interface LightTaskAnalyzeItem {
  uuid: string;
  candidateName: string;
  status: string;
  action: 'analyzed' | 'reused' | 'skipped' | 'failed';
  reason?: string | null;
  matchScore?: number | null;
  matchAnalysis?: LightRecommendationItem['matchAnalysis'];
}

export interface LightTaskAnalyzeResult {
  taskUuid: string;
  total: number;
  analyzed: number;
  reused: number;
  skipped: number;
  failed: number;
  items: LightTaskAnalyzeItem[];
}

export interface LightRecommendationItem {
  uuid: string;
  taskUuid: string;
  taskTitle?: string | null;
  jobTitle?: string | null;
  recommenderMemberUuid: string;
  recommenderName?: string | null;
  talentSource: 'hunter_talent' | 'c_talent' | 'manual';
  talentUuid?: string | null;
  cTalentUuid?: string | null;
  projectCandidateUuid?: string | null;
  projectUuid?: string | null;
  candidateName: string;
  candidatePhone?: string | null;
  candidateCompany?: string | null;
  candidatePosition?: string | null;
  status: LightRecommendationStatus;
  rewardMode?: 'points' | 'cash_legacy';
  rewardStages?: LightHunterRewardStage[] | null;
  totalPoints?: number | null;
  commissionAmount?: number | null;
  matchScore?: number | null;
  matchSource?: 'resume_ai' | 'legacy_manual' | null;
  resumeAnalysisUuid?: string | null;
  resumeFileName?: string | null;
  resumeFileType?: string | null;
  resumeFileSize?: number | null;
  resumeParseStatus?: 'pending' | 'processing' | 'completed' | 'failed' | null;
  matchStatus?: 'pending' | 'processing' | 'completed' | 'failed' | null;
  parsedResumeSnapshot?: {
    name?: string;
    phone?: string;
    email?: string;
    city?: string;
    currentCompany?: string;
    currentPosition?: string;
    yearsOfExperience?: string;
    highestEducation?: string;
    skills?: string[];
    workExperiences?: Array<Record<string, string>>;
    projectExperiences?: Array<Record<string, string>>;
    educationExperiences?: Array<Record<string, string>>;
    summary?: string;
  } | null;
  matchAnalysis?: {
    score?: number;
    level?: 'high' | 'medium' | 'low';
    summary?: string;
    dimensions?: Record<string, number | null>;
    strengths?: Array<{ title: string; evidence?: string }>;
    gaps?: Array<{ title: string; evidence?: string }>;
    constraints?: Array<{ title: string; evidence?: string }>;
    evidence?: string[];
    model?: string;
    version?: string;
  } | null;
  resumeParseError?: string | null;
  matchError?: string | null;
  analyzedAt?: string | null;
  analysisVersion?: string | null;
  recommendationReason?: string | null;
  clientFeedback?: string | null;
  recommendedAt?: string | null;
}

export interface LightHunterConversationItem {
  uuid: string;
  conversationID: string;
  hunterConversationID?: string;
  hunterUserUuid: string;
  hunterMemberUuid: string;
  hunterName?: string | null;
  hunterAvatarUrl?: string | null;
  assignedMemberUuid: string;
  hunterImAccountId: string;
  assignedImAccountId: string;
  currentTaskUuid?: string | null;
  currentTaskTitle?: string | null;
  updatedAt?: string;
}

export interface LightRecommendationDetail extends LightRecommendationItem {
  resumeFileUrl?: string | null;
  history?: Array<{
    uuid: string;
    fromStatus?: LightRecommendationStatus | null;
    toStatus: LightRecommendationStatus;
    note?: string | null;
    changedAt: string;
  }>;
  jobSnapshot?: {
    title?: string | null;
    description?: string | null;
    recommendationRequirement?: string | null;
    educationRequirement?: string | null;
    experienceRequirement?: string | null;
    location?: string | null;
    salaryMin?: number | null;
    salaryMax?: number | null;
    salaryUnit?: string | null;
  } | null;
}

export interface CreateRecommendationPayload {
  taskUuid: string;
  claimUuid?: string;
  talentSource?: 'hunter_talent' | 'c_talent' | 'manual';
  talentUuid?: string;
  cTalentUuid?: string;
  candidateName: string;
  candidatePhone?: string;
  candidateCompany?: string;
  candidatePosition?: string;
  matchScore?: number;
  recommendationReason?: string;
}

export interface LightMemberItem {
  memberUuid: string;
  displayName: string;
  avatarUrl?: string | null;
  organizationRole?: string;
  status?: string;
  role: LightMemberRole;
  enabled: boolean;
  notes?: string | null;
}

export interface LightBusinessTenantItem {
  uuid: string;
  displayName: string;
  status: 'active' | 'suspended' | 'closed';
  isPersonal: boolean;
  ownerUserUuid?: string | null;
  memberCount: number;
  wallet: {
    balance: number;
    frozenBalance: number;
    totalEarned: number;
  };
}

export interface LightHunterAnalyticsOverview {
  period: 'today' | '7d' | '30d';
  generatedAt: string;
  enterprises: LightEnterpriseDirectorySummary;
  tasks: Record<LightTaskStatus, number> & { publishedInPeriod?: number };
  funnel: {
    submitted: number;
    bdScreenPassed: number;
    interviewing: number;
    hired: number;
  };
  industries: Array<{ name: string; count: number }>;
  memberPerformance: Array<{
    memberUuid: string;
    memberName: string;
    claims: number;
    recommendations: number;
    interviews: number;
    hired: number;
  }>;
  finance: {
    settledAmount: number;
    settledPoints?: number;
    pendingSettlementAmount: number;
    pendingWithdrawalAmount: number;
    paidWithdrawalAmount: number;
  };
}

export type LightVerificationStatus =
  | 'draft'
  | 'pending'
  | 'approved'
  | 'rejected';
export interface LightVerificationItem {
  uuid: string;
  memberUuid: string;
  userUuid: string;
  fullName: string;
  phone?: string | null;
  idNumber: string;
  frontImageUrl?: string | null;
  backImageUrl?: string | null;
  status: LightVerificationStatus;
  rejectionReason?: string | null;
  reviewNote?: string | null;
  reviewerMemberUuid?: string | null;
  reviewerUserUuid?: string | null;
  submittedAt?: string | null;
  reviewedAt?: string | null;
}

export interface LightHunterDirectoryItem {
  uuid: string;
  memberUuid: string;
  userUuid: string;
  fullName: string;
  phone?: string | null;
  avatarUrl?: string | null;
  verifiedAt?: string | null;
  online?: boolean;
  levelCode?: string | null;
  levelName?: string | null;
  claimCount: number;
  taskCount: number;
  recommendationCount: number;
  hiredCount: number;
  lastConversationAt?: string | null;
  conversationID?: string | null;
  blacklisted?: boolean;
}

export interface LightHunterDetail {
  memberUuid: string;
  userUuid?: string | null;
  fullName: string;
  phone?: string | null;
  email?: string | null;
  username?: string | null;
  avatarUrl?: string | null;
  verifiedAt?: string | null;
  levelCode?: string | null;
  levelName?: string | null;
  effectiveRecommendations?: number;
  interviews?: number;
  hires?: number;
  blacklisted?: boolean;
  blacklistReason?: string | null;
  notes?: Array<{
    content: string;
    createdAt: string;
    createdByName?: string | null;
  }>;
  verification?: {
    uuid: string;
    fullName: string;
    idNumberMasked?: string | null;
    status: string;
    submittedAt?: string | null;
    reviewedAt?: string | null;
    frontImageUrl?: string | null;
    backImageUrl?: string | null;
  } | null;
  points?: {
    availablePoints: number;
    totalPoints: number;
    frozenPoints: number;
    exchangedPoints: number;
    ledger: Array<{
      uuid: string;
      points: number;
      direction: string;
      description?: string | null;
      occurredAt?: string | null;
    }>;
  };
  withdrawals?: Array<{
    uuid: string;
    amount: number;
    points?: number | null;
    status: string;
    createdAt?: string;
    paidAt?: string | null;
  }>;
  recommendations?: Array<{
    uuid: string;
    candidateName: string;
    status: string;
    taskUuid?: string | null;
    taskTitle?: string | null;
    createdAt?: string;
  }>;
  claims?: Array<{
    uuid: string;
    taskUuid?: string | null;
    taskTitle?: string | null;
    status: string;
    claimedAt?: string | null;
  }>;
}

export interface LightWithdrawalItem {
  uuid: string;
  memberUuid: string;
  memberName?: string | null;
  memberPhone?: string | null;
  memberEmail?: string | null;
  memberUsername?: string | null;
  amount: number;
  points?: number | null;
  status: 'pending' | 'approved' | 'paid' | 'rejected' | 'failed';
  reviewerMemberUuid?: string | null;
  reviewNote?: string | null;
  paymentReference?: string | null;
  reviewedAt?: string | null;
  paidAt?: string | null;
  createdAt?: string;
}

export interface LightSettlementItem {
  uuid: string;
  recommendationUuid: string;
  taskUuid?: string | null;
  stageCode?: string | null;
  stageLabel?: string | null;
  memberUuid: string;
  memberName?: string | null;
  candidateName?: string | null;
  taskTitle?: string | null;
  businessTenantUuid?: string | null;
  businessTenantName?: string | null;
  amount: number;
  pointsAmount?: number | null;
  rewardMode?: 'points' | 'cash_legacy' | null;
  status: 'pending' | 'confirmed' | 'cancelled';
  note?: string | null;
  confirmedAt?: string | null;
  createdAt?: string;
}

export interface LightPointsLedgerItem {
  uuid: string;
  memberUuid?: string | null;
  taskUuid?: string | null;
  recommendationUuid?: string | null;
  stageCode?: string | null;
  direction: 'credit' | 'debit';
  points: number;
  balanceAfter?: number | null;
  bizType?: string | null;
  description?: string | null;
  occurredAt?: string | null;
}

export interface LightPointsExchangeItem {
  uuid: string;
  memberUuid?: string | null;
  points: number;
  cashAmount: number;
  exchangeRate?: number | null;
  exchangedAt?: string | null;
}

const base = '/bff/luole-hunter/light-hunter';

export type LightHunterRewardRule = {
  mode: 'percent' | 'fixed';
  percent: number;
  points: number;
};

export type LightHunterRewardSettings = {
  pointsPerCny: number;
  split: Record<string, number>;
  rules: Record<string, LightHunterRewardRule>;
  stages: Array<LightHunterRewardStage & LightHunterRewardRule>;
};

export const getLightHunterSettings = () =>
  request<LightHunterRewardSettings>(`${base}/settings`);

export const updateLightHunterSettings = (data: {
  rules?: Record<string, LightHunterRewardRule>;
  split?: Record<string, number>;
}) =>
  request<LightHunterRewardSettings>(`${base}/settings`, {
    method: 'POST',
    data,
  });

export const lightHunterApi = {
  getAccess: () => request<LightHunterAccess>(`${base}/access`),
  getSettings: getLightHunterSettings,
  updateSettings: updateLightHunterSettings,
  getDashboard: () => request<LightHunterDashboard>(`${base}/dashboard`),

  getCompanies: (
    params?: PageParams & {
      status?: LightCompanyStatus;
      bdMemberUuid?: string;
    },
  ) =>
    request<PageResponse<LightCompanyPoolItem>>(`${base}/companies`, {
      params,
    }),

  getTaskCompanies: () =>
    request<{ data: LightTaskCompanyOption[] }>(`${base}/task-companies`),

  getEnterprises: (
    params?: PageParams & {
      status?: 'unverified' | 'pending' | 'verified' | 'rejected';
      onlyUnreleased?: boolean;
      industry?: string;
    },
  ) =>
    request<PageResponse<LightEnterpriseItem>>(`${base}/enterprises`, {
      params,
    }),

  getEnterpriseDirectory: (
    params?: PageParams & {
      releaseState?: LightEnterpriseReleaseState;
      channelStatus?: LightCompanyStatus;
      sourceType?: LightEnterpriseSourceType;
      industry?: string;
      bdMemberUuid?: string;
      progressStage?: LightEnterpriseProgressStage;
    },
  ) =>
    request<LightEnterpriseDirectoryResponse>(`${base}/enterprise-directory`, {
      params,
    }),

  getEnterpriseDetail: (certificationUuid: string) =>
    request<LightEnterpriseDetail>(`${base}/enterprises/${certificationUuid}`),

  getEnterpriseMetrics: () =>
    request<LightEnterpriseMetrics>(`${base}/enterprises/metrics`),

  getReleasedJobPostings: (
    params?: PageParams & { search?: string; publishedAsTask?: boolean },
  ) =>
    request<PageResponse<LightReleasedJobPostingItem>>(`${base}/job-postings`, {
      params,
    }),

  getTalents: (
    params?: PageParams & {
      city?: string;
      expectJob?: string;
      workYear?: string;
      isPublic?: boolean;
    },
  ) =>
    request<PageResponse<LightTalentItem>>(`${base}/talents`, {
      params,
    }),

  getTalentDetail: (resumeUuid: string) =>
    request<LightTalentItem>(`${base}/talents/${resumeUuid}`),

  releaseEnterprise: (
    certificationUuid: string,
    data?: ReleaseEnterprisePayload,
  ) =>
    request<LightCompanyPoolItem>(
      `${base}/enterprises/${certificationUuid}/release`,
      {
        method: 'POST',
        data,
      },
    ),

  previewEnterpriseRelease: (
    certificationUuid: string,
    data?: ReleaseEnterprisePayload,
  ) =>
    request<LightEnterpriseReleasePreview>(
      `${base}/enterprises/${certificationUuid}/release/preview`,
      { method: 'POST', data },
    ),

  batchReleaseEnterprises: (data: BatchReleaseEnterprisePayload) =>
    request<BatchReleaseEnterpriseResult>(`${base}/enterprises/batch-release`, {
      method: 'POST',
      data,
    }),

  releaseCompany: (data: ReleaseCompanyPayload) =>
    request<LightCompanyPoolItem>(`${base}/companies/release`, {
      method: 'POST',
      data,
    }),

  withdrawCompany: (uuid: string) =>
    request<LightCompanyPoolItem>(`${base}/companies/${uuid}/withdraw`, {
      method: 'POST',
    }),

  reopenCompany: (uuid: string) =>
    request<LightCompanyPoolItem>(`${base}/companies/${uuid}/reopen`, {
      method: 'POST',
    }),

  claimCompany: (uuid: string, bdMemberUuid?: string) =>
    request<LightCompanyPoolItem>(`${base}/companies/${uuid}/claim`, {
      method: 'POST',
      data: { bdMemberUuid },
    }),

  transferCompany: (uuid: string, bdMemberUuid: string) =>
    request<LightCompanyPoolItem>(`${base}/companies/${uuid}/transfer`, {
      method: 'POST',
      data: { bdMemberUuid },
    }),

  getTasks: (
    params?: PageParams & {
      status?: LightTaskStatus;
      companyPoolUuid?: string;
      projectUuid?: string;
      sortBy?: 'updatedAt' | 'createdAt' | 'publishedAt';
      salaryMin?: number;
      salaryMax?: number;
    },
  ) =>
    request<PageResponse<LightTaskItem> & { summary?: LightTaskListSummary }>(
      `${base}/tasks`,
      { params },
    ),

  getTask: (uuid: string) => request<LightTaskItem>(`${base}/tasks/${uuid}`),

  createTask: (data: CreateTaskPayload) =>
    request<LightTaskItem>(`${base}/tasks`, { method: 'POST', data }),

  updateTask: (uuid: string, data: Partial<CreateTaskPayload>) =>
    request<LightTaskItem>(`${base}/tasks/${uuid}`, { method: 'PATCH', data }),

  deleteTask: (uuid: string) =>
    request<{ success: true }>(`${base}/tasks/${uuid}`, { method: 'DELETE' }),

  publishTask: (uuid: string, data?: { createProject?: boolean }) =>
    request<LightTaskItem>(`${base}/tasks/${uuid}/publish`, {
      method: 'POST',
      data: data || {},
    }),

  bindTaskProject: (uuid: string, projectUuid?: string | null) =>
    request<LightTaskItem & { projectSync?: LightTaskProjectSyncSummary }>(
      `${base}/tasks/${uuid}/bind-project`,
      { method: 'POST', data: { projectUuid: projectUuid || null } },
    ),

  analyzeTaskRecommendations: (
    uuid: string,
    data?: { recommendationUuids?: string[]; forceRefresh?: boolean },
  ) =>
    request<LightTaskAnalyzeResult>(
      `${base}/tasks/${uuid}/analyze-recommendations`,
      { method: 'POST', data: data || {} },
    ),

  closeTask: (uuid: string, status?: LightTaskStatus) =>
    request<LightTaskItem>(`${base}/tasks/${uuid}/close`, {
      method: 'POST',
      data: { status },
    }),

  claimTask: (uuid: string) =>
    request(`${base}/tasks/${uuid}/claim`, { method: 'POST' }),

  cancelClaim: (uuid: string, reason?: string) =>
    request(`${base}/claims/${uuid}/cancel`, {
      method: 'POST',
      data: { reason },
    }),

  initiateConversation: (data: {
    taskUuid?: string;
    recommendationUuid?: string;
    hunterMemberUuid?: string;
  }) =>
    request<LightHunterConversationItem>(`${base}/conversations/initiate`, {
      method: 'POST',
      data,
    }),

  getCandidates: (params?: PageParams) =>
    request<
      PageResponse<{
        candidateName: string;
        candidatePhone?: string | null;
        candidatePosition?: string | null;
        candidateCompany?: string | null;
        recommendationCount: number;
        latestTaskTitle?: string | null;
        latestTaskUuid?: string | null;
        latestStatus: LightRecommendationStatus;
        latestMatchScore?: number | null;
        latestRecommenderName?: string | null;
        latestRecommenderMemberUuid?: string | null;
        latestRecommendedAt?: string | null;
        latestRecommendationUuid?: string | null;
        highestEducation?: string | null;
        yearsOfExperience?: string | null;
        city?: string | null;
        expectedJob?: string | null;
        expectedLocation?: string | null;
        skills?: string[];
        resumeFileUrl?: string | null;
        resumeFileName?: string | null;
        parsedResumeSnapshot?: LightRecommendationItem['parsedResumeSnapshot'];
      }>
    >(`${base}/candidates`, { params }),

  getConversations: () =>
    request<{ data: LightHunterConversationItem[] }>(`${base}/conversations`),

  getConversationMeta: (conversationIDs: string[]) =>
    request<{ data: LightHunterConversationItem[] }>(
      `${base}/conversations/meta`,
      {
        method: 'POST',
        data: { conversationIDs },
      },
    ),

  getImLoginInfo: () =>
    request<{
      sdkAppId: number;
      userId: string;
      userSig: string;
      expireSeconds?: number;
    }>('/bff/auth/im-login-info'),

  getRecommendations: (
    params?: PageParams & {
      taskUuid?: string;
      status?: LightRecommendationStatus;
      recommenderMemberUuid?: string;
    },
  ) =>
    request<PageResponse<LightRecommendationItem>>(`${base}/recommendations`, {
      params,
    }),

  getRecommendation: (uuid: string) =>
    request<LightRecommendationDetail>(`${base}/recommendations/${uuid}`),

  createRecommendation: (data: CreateRecommendationPayload) =>
    request<LightRecommendationItem>(`${base}/recommendations`, {
      method: 'POST',
      data,
    }),

  updateRecommendationStatus: (
    uuid: string,
    data: { status: LightRecommendationStatus; clientFeedback?: string },
  ) =>
    request<
      LightRecommendationItem & { projectSync?: LightRecommendationProjectSync }
    >(`${base}/recommendations/${uuid}/status`, {
      method: 'POST',
      data,
    }),

  getMembers: () => request<LightMemberItem[]>(`${base}/members`),

  getHunters: (params?: PageParams) =>
    request<PageResponse<LightHunterDirectoryItem>>(`${base}/hunters`, {
      params,
    }),

  getHunterDetail: (memberUuid: string) =>
    request<LightHunterDetail>(`${base}/hunters/${memberUuid}`),

  addHunterNote: (memberUuid: string, note: string) =>
    request<LightHunterDetail>(`${base}/hunters/${memberUuid}/notes`, {
      method: 'POST',
      data: { note },
    }),

  updateHunterBlacklist: (
    memberUuid: string,
    data: { blacklisted: boolean; reason?: string },
  ) =>
    request<LightHunterDetail>(`${base}/hunters/${memberUuid}/blacklist`, {
      method: 'POST',
      data,
    }),

  getBusinessTenants: () =>
    request<LightBusinessTenantItem[]>(`${base}/business-tenants`),

  getAnalyticsOverview: (period: 'today' | '7d' | '30d') =>
    request<LightHunterAnalyticsOverview>(`${base}/analytics/overview`, {
      params: { period },
    }),

  getVerifications: (
    params?: PageParams & { status?: LightVerificationStatus },
  ) =>
    request<PageResponse<LightVerificationItem>>(`${base}/verifications`, {
      params,
    }),

  getVerification: (uuid: string) =>
    request<LightVerificationItem>(`${base}/verifications/${uuid}`),

  approveVerification: (uuid: string, note?: string) =>
    request<LightVerificationItem>(`${base}/verifications/${uuid}/approve`, {
      method: 'POST',
      data: { note },
    }),

  rejectVerification: (uuid: string, note: string) =>
    request<LightVerificationItem>(`${base}/verifications/${uuid}/reject`, {
      method: 'POST',
      data: { note },
    }),

  getWithdrawals: (
    params?: PageParams & { status?: LightWithdrawalItem['status'] },
  ) =>
    request<PageResponse<LightWithdrawalItem>>(`${base}/withdrawals`, {
      params,
    }),

  getSettlements: (params?: PageParams) =>
    request<PageResponse<LightSettlementItem>>(`${base}/settlements`, {
      params,
    }),
  getPointsLedger: (params?: PageParams) =>
    request<PageResponse<LightPointsLedgerItem>>(`${base}/points/ledger`, {
      params,
    }),
  getPointsExchanges: (params?: PageParams) =>
    request<PageResponse<LightPointsExchangeItem>>(`${base}/points/exchanges`, {
      params,
    }),

  reviewWithdrawal: (
    uuid: string,
    status: 'approved' | 'rejected',
    note?: string,
  ) =>
    request<LightWithdrawalItem>(`${base}/withdrawals/${uuid}/review`, {
      method: 'POST',
      data: { status, note },
    }),

  completeWithdrawal: (
    uuid: string,
    data: {
      status: 'paid' | 'failed';
      paymentReference?: string;
      note?: string;
    },
  ) =>
    request<LightWithdrawalItem>(`${base}/withdrawals/${uuid}/payment`, {
      method: 'POST',
      data,
    }),

  confirmSettlement: (uuid: string, amount: number, note?: string) =>
    request(`${base}/recommendations/${uuid}/settlement`, {
      method: 'POST',
      data: { amount, note },
    }),

  getRecommendationHistory: (uuid: string) =>
    request(`${base}/recommendations/${uuid}/history`),

  upsertMemberRole: (data: {
    memberUuid: string;
    role: LightMemberRole;
    enabled?: boolean;
    notes?: string;
  }) => request(`${base}/members/role`, { method: 'POST', data }),
};
